import { useCallback } from 'react';

import { randomUUID } from 'expo-crypto';
import { useStoreContext } from 'features/store';

import { ICartEntry } from '@rbi-ctg/menu';
import { useHydrateCartMutation } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { validateAppliedReward } from 'state/global-state/models/loyalty/rewards/rewards.slice';
import { IMinimalCartEntry } from 'state/global-state/models/ordering/cart/cart.types';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useServiceModeContext } from 'state/service-mode';
import { findRewardBenefitFromHydratedCartEntry } from 'utils/minimal-cart';

import { hydrateEntries } from './hydrateEntries';
import { useCart } from './use-cart';

type TRewardMinimalCartEntry = IMinimalCartEntry &
  Required<Pick<IMinimalCartEntry, 'rewardId' | 'cartId'>>;

export interface IUseMinimalCart {
  cartEntries: IMinimalCartEntry[];
  addItemToMinimalCart: (cartEntry: IMinimalCartEntry) => void;
  emptyMinimalCart: () => void;
  hydrateMinimalCart: (entries?: IMinimalCartEntry[]) => Promise<boolean>;
  getAppliedReward: () => TRewardMinimalCartEntry | undefined;
}

/**
 * Expose the cart state and the utility methods to mutate it.
 *
 * When an action to mutate the cart is triggered, it manages the Redux cart actions, logs the events,
 * coordinates with other parts of the code, and dispatches actions on related slices.
 */
export const useMinimalCart = (): IUseMinimalCart => {
  const dispatch = useAppDispatch();
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const totalTimesRewardApplied = useAppSelector(selectors.loyalty.selectTotalTimesRewardApplied);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { serviceMode } = useServiceModeContext();
  const { isAuthenticated } = useAuthContext();
  const [hydrateCart] = useHydrateCartMutation();
  const { addItemToCart, updateCartEntry } = useCart();

  // Adds a single cartEntry to the cart
  const addItemToMinimalCart = useCallback<IUseMinimalCart['addItemToMinimalCart']>(
    minimalCartEntry => {
      dispatch(actions.ordering.addMinimalCartEntry(minimalCartEntry));
    },
    [dispatch]
  );

  const emptyMinimalCart = useCallback<IUseMinimalCart['emptyMinimalCart']>(() => {
    dispatch(actions.ordering.resetMinimalCart());
  }, [dispatch]);

  const handleRewards = useCallback(
    (rewardBenefitId: string, cartEntry: ICartEntry, remainingPoints: { value: number }) => {
      let cartEntryWithReward = { ...cartEntry };

      const { isApplicable, mappedReward } = validateAppliedReward(
        availableLoyaltyRewardsMap,
        rewardBenefitId,
        totalTimesRewardApplied,
        1,
        remainingPoints.value
      );

      if (isApplicable) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId,
            cartId: cartEntryWithReward.cartId,
            quantity: 1,
            redemptionMethod: RewardRedemptionMethod.MOBILE,
          })
        );
        remainingPoints.value -= mappedReward?.pointCost ?? 0;
      } else {
        const rewardBenefit = findRewardBenefitFromHydratedCartEntry(cartEntry);
        if (rewardBenefit) {
          cartEntryWithReward = { ...rewardBenefit };
          const newId = randomUUID();
          cartEntryWithReward.cartId = newId;
          // @ts-ignore
          cartEntryWithReward.lineId = newId;
        }
      }
      return cartEntryWithReward;
    },
    [availableLoyaltyRewardsMap, dispatch, totalTimesRewardApplied]
  );

  const hydrateMinimalCart = useCallback(
    async (entries?: IMinimalCartEntry[]) => {
      if (!storeId || !serviceMode || !isAuthenticated) {
        return false;
      }

      return hydrateEntries(
        entries ?? minimalCartEntries,
        storeId,
        serviceMode,
        hydrateCart,
        addItemToCart,
        updateCartEntry,
        dispatch,
        handleRewards,
        stagedCartPoints
      );
    },
    [
      addItemToCart,
      dispatch,
      handleRewards,
      hydrateCart,
      isAuthenticated,
      minimalCartEntries,
      serviceMode,
      stagedCartPoints,
      storeId,
      updateCartEntry,
    ]
  );

  const getAppliedReward = useCallback(() => {
    return minimalCartEntries.find(
      (entry): entry is TRewardMinimalCartEntry => !!entry.rewardId && !!entry.cartId
    );
  }, [minimalCartEntries]);

  return {
    cartEntries: minimalCartEntries,
    addItemToMinimalCart,
    emptyMinimalCart,
    hydrateMinimalCart,
    getAppliedReward,
  };
};
